import { useState, useEffect } from 'react';
import axios from 'axios';
import { motion } from 'framer-motion';
import { Gift, Newspaper, Check, Trash2, Save, Send, RefreshCw } from 'lucide-react';
import Sidebar from '@/components/Sidebar';
import { useGuild } from '@/contexts/GuildContext';
import { useAuth } from '@/contexts/AuthContext';
import { toast } from '@/components/ui/sonner';

const API = process.env.REACT_APP_BACKEND_URL;

const PLATFORMS = [
  { key: 'steam', label: 'Steam', color: '#1B2838', badge: 'bg-[#1B2838] border-[#4C6B22]/50 text-[#A8D56A]' },
  { key: 'epic',  label: 'Epic Games', color: '#2F2F2F', badge: 'bg-zinc-800 border-zinc-600 text-white' },
  { key: 'gog',   label: 'GOG', color: '#86410A', badge: 'bg-orange-950/60 border-orange-700/40 text-orange-300' },
];

const GAMES = [
  { key: 'cs2',          label: 'Counter-Strike 2', color: '#F0A500' },
  { key: 'dota2',        label: 'Dota 2',           color: '#C23C2A' },
  { key: 'apex',         label: 'Apex Legends',     color: '#CD3333' },
  { key: 'rust',         label: 'Rust',             color: '#C04B1C' },
  { key: 'deadlock',     label: 'Deadlock',         color: '#4A9EBF' },
  { key: 'tf2',          label: 'Team Fortress 2',  color: '#CF5E2B' },
  { key: 'rocketleague', label: 'Rocket League',    color: '#1A9FFF' },
  { key: 'pubg',         label: 'PUBG',             color: '#F5A623' },
  { key: 'lol',          label: 'League of Legends', color: '#C89B3C' },
];

function authHeader() {
  const token = localStorage.getItem('token');
  return token ? { headers: { Authorization: `Bearer ${token}` } } : {};
}

function ChannelSelect({ channels, value, onChange }) {
  if (channels === null) {
    return <p className="text-zinc-600 text-xs font-[Manrope]">Loading channels…</p>;
  }
  if (channels.length === 0) {
    return <p className="text-zinc-600 text-xs font-[Manrope]">No text channels found.</p>;
  }
  return (
    <select
      value={value}
      onChange={e => onChange(e.target.value)}
      className="w-full bg-[var(--bg-hover)] border border-[var(--border-md)] rounded-lg px-3 py-2 text-sm text-white font-[Manrope] focus:outline-none focus:border-[#5865F2]/60"
    >
      <option value="" className="bg-[var(--bg-surface)] text-white">Select a channel…</option>
      {channels.map(ch => (
        <option key={ch.id} value={ch.id} className="bg-[var(--bg-surface)] text-white">#{ch.name}</option>
      ))}
    </select>
  );
}

function FreeGameAlerts({ guildId }) {
  const [channels, setChannels] = useState(null);
  const [channelId, setChannelId] = useState('');
  const [platforms, setPlatforms] = useState(['steam', 'epic', 'gog']);
  const [enabled, setEnabled] = useState(true);
  const [saved, setSaved] = useState(false);
  const [saving, setSaving] = useState(false);
  const [hasConfig, setHasConfig] = useState(false);

  useEffect(() => {
    if (!guildId) return;
    axios.get(`${API}/api/announce/channels/${guildId}`, authHeader())
      .then(r => setChannels(r.data.channels || []))
      .catch(() => setChannels([]));
    axios.get(`${API}/api/game-alerts/free-games/${guildId}`, authHeader())
      .then(r => {
        if (r.data && r.data.channel_id) {
          setChannelId(r.data.channel_id);
          setPlatforms(r.data.platforms || ['steam', 'epic', 'gog']);
          setEnabled(r.data.enabled !== false);
          setHasConfig(true);
        }
      })
      .catch(() => {});
  }, [guildId]);

  const togglePlatform = key => {
    setPlatforms(prev =>
      prev.includes(key) ? prev.filter(p => p !== key) : [...prev, key]
    );
  };

  const [testing, setTesting] = useState(false);

  const handleSave = async () => {
    if (!channelId || platforms.length === 0) return;
    setSaving(true);
    try {
      await axios.put(`${API}/api/game-alerts/free-games`, {
        guild_id: guildId, channel_id: channelId, platforms, enabled,
      }, authHeader());
      setHasConfig(true);
      setSaved(true);
      toast.success('Free Game Alerts settings saved');
      setTimeout(() => setSaved(false), 2500);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    try {
      await axios.delete(`${API}/api/game-alerts/free-games/${guildId}`, authHeader());
      toast.success('Free Game Alerts disabled');
    } catch {
      toast.error('Failed to remove configuration');
    }
    setChannelId('');
    setPlatforms(['steam', 'epic', 'gog']);
    setEnabled(true);
    setHasConfig(false);
  };

  const handleTest = async () => {
    if (!channelId) return;
    setTesting(true);
    try {
      await axios.post(`${API}/api/game-alerts/free-games/test`, {
        guild_id: guildId, channel_id: channelId,
      }, authHeader());
      toast.success('Test alert sent! Check your Discord channel.');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to send test alert');
    } finally {
      setTesting(false);
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}
      className="bg-[var(--bg-surface)] border border-[var(--border)] rounded-xl p-6"
    >
      <div className="flex items-start justify-between mb-5">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-[#10B981]/15 flex items-center justify-center">
            <Gift size={20} className="text-[#10B981]" />
          </div>
          <div>
            <h2 className="text-base font-semibold text-white font-[Outfit]">Free Game Alerts</h2>
            <p className="text-xs text-zinc-500 font-[Manrope]">Posts new free game giveaways to a channel automatically</p>
          </div>
        </div>
        {hasConfig && (
          <button
            onClick={handleDelete}
            className="p-2 rounded-lg text-zinc-600 hover:text-red-400 hover:bg-red-500/10 transition-colors"
            title="Remove configuration"
          >
            <Trash2 size={16} />
          </button>
        )}
      </div>

      <div className="space-y-5">
        {/* Platform toggles */}
        <div>
          <p className="text-xs font-semibold text-zinc-500 uppercase tracking-widest mb-3 font-[Manrope]">Platforms to watch</p>
          <div className="flex flex-wrap gap-2">
            {PLATFORMS.map(p => (
              <button
                key={p.key}
                onClick={() => togglePlatform(p.key)}
                className={`flex items-center gap-2 px-3 py-1.5 rounded-lg border text-xs font-medium font-[Manrope] transition-all ${
                  platforms.includes(p.key)
                    ? p.badge + ' opacity-100'
                    : 'bg-[var(--bg-hover)] border-[var(--border)] text-zinc-500 opacity-60'
                }`}
              >
                {platforms.includes(p.key) && <Check size={12} />}
                {p.label}
              </button>
            ))}
          </div>
        </div>

        {/* Channel */}
        <div>
          <p className="text-xs font-semibold text-zinc-500 uppercase tracking-widest mb-2 font-[Manrope]">Post to channel</p>
          <ChannelSelect channels={channels} value={channelId} onChange={setChannelId} />
        </div>

        {/* Enable toggle */}
        <div className="flex items-center justify-between">
          <span className="text-sm text-zinc-400 font-[Manrope]">Alerts enabled</span>
          <button
            onClick={() => setEnabled(e => !e)}
            className={`relative w-11 h-6 rounded-full transition-colors ${enabled ? 'bg-[#10B981]' : 'bg-white/10'}`}
          >
            <span className={`absolute top-1 w-4 h-4 bg-white rounded-full shadow transition-all ${enabled ? 'left-6' : 'left-1'}`} />
          </button>
        </div>

        <div className="flex gap-2">
          <button
            onClick={handleSave}
            disabled={saving || !channelId || platforms.length === 0}
            className="flex-1 flex items-center justify-center gap-2 py-2.5 rounded-lg bg-[#10B981] hover:bg-[#0d9e71] disabled:opacity-40 disabled:cursor-not-allowed text-sm font-semibold text-white font-[Outfit] transition-colors"
          >
            {saved ? <><Check size={16} /> Saved!</> : saving ? 'Saving…' : <><Save size={16} /> Save</>}
          </button>
          <button
            onClick={handleTest}
            disabled={testing || !channelId}
            title="Send a sample alert to the configured channel"
            className="flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg border border-[var(--border-md)] hover:bg-[var(--bg-hover)] disabled:opacity-40 disabled:cursor-not-allowed text-sm font-semibold text-zinc-300 font-[Outfit] transition-colors"
          >
            <Send size={15} />
            {testing ? 'Sending…' : 'Test'}
          </button>
        </div>
      </div>
    </motion.div>
  );
}

function PatchNoteAlerts({ guildId }) {
  const [channels, setChannels] = useState(null);
  const [channelId, setChannelId] = useState('');
  const [games, setGames] = useState([]);
  const [enabled, setEnabled] = useState(true);
  const [saved, setSaved] = useState(false);
  const [saving, setSaving] = useState(false);
  const [hasConfig, setHasConfig] = useState(false);

  useEffect(() => {
    if (!guildId) return;
    axios.get(`${API}/api/announce/channels/${guildId}`, authHeader())
      .then(r => setChannels(r.data.channels || []))
      .catch(() => setChannels([]));
    axios.get(`${API}/api/game-alerts/patch-notes/${guildId}`, authHeader())
      .then(r => {
        if (r.data && r.data.channel_id) {
          setChannelId(r.data.channel_id);
          setGames(r.data.games || []);
          setEnabled(r.data.enabled !== false);
          setHasConfig(true);
        }
      })
      .catch(() => {});
  }, [guildId]);

  const toggleGame = key => {
    setGames(prev =>
      prev.includes(key) ? prev.filter(g => g !== key) : [...prev, key]
    );
  };

  const [testing, setTesting] = useState(false);

  const handleSave = async () => {
    if (!channelId || games.length === 0) return;
    setSaving(true);
    try {
      await axios.put(`${API}/api/game-alerts/patch-notes`, {
        guild_id: guildId, channel_id: channelId, games, enabled,
      }, authHeader());
      setHasConfig(true);
      setSaved(true);
      toast.success('Patch Note Alerts settings saved');
      setTimeout(() => setSaved(false), 2500);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    try {
      await axios.delete(`${API}/api/game-alerts/patch-notes/${guildId}`, authHeader());
      toast.success('Patch Note Alerts disabled');
    } catch {
      toast.error('Failed to remove configuration');
    }
    setChannelId('');
    setGames([]);
    setEnabled(true);
    setHasConfig(false);
  };

  const handleTest = async () => {
    if (!channelId) return;
    setTesting(true);
    try {
      await axios.post(`${API}/api/game-alerts/patch-notes/test`, {
        guild_id: guildId, channel_id: channelId,
      }, authHeader());
      toast.success('Test alert sent! Check your Discord channel.');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to send test alert');
    } finally {
      setTesting(false);
    }
  };

  const [checkingLol, setCheckingLol] = useState(false);
  const handleCheckLolNow = async () => {
    setCheckingLol(true);
    try {
      await axios.post(`${API}/api/game-alerts/patch-notes/check-now`, null, {
        ...authHeader(), params: { guild_id: guildId, game: 'lol' },
      });
      toast.success('Posted the latest League of Legends patch notes!');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to fetch patch notes');
    } finally {
      setCheckingLol(false);
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }}
      className="bg-[var(--bg-surface)] border border-[var(--border)] rounded-xl p-6"
    >
      <div className="flex items-start justify-between mb-5">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-[#5865F2]/15 flex items-center justify-center">
            <Newspaper size={20} className="text-[#5865F2]" />
          </div>
          <div>
            <h2 className="text-base font-semibold text-white font-[Outfit]">Patch Note Alerts</h2>
            <p className="text-xs text-zinc-500 font-[Manrope]">Auto-posts balance patches and game updates via Steam RSS</p>
          </div>
        </div>
        {hasConfig && (
          <button
            onClick={handleDelete}
            className="p-2 rounded-lg text-zinc-600 hover:text-red-400 hover:bg-red-500/10 transition-colors"
            title="Remove configuration"
          >
            <Trash2 size={16} />
          </button>
        )}
      </div>

      <div className="space-y-5">
        {/* Game grid */}
        <div>
          <p className="text-xs font-semibold text-zinc-500 uppercase tracking-widest mb-3 font-[Manrope]">
            Games to watch — select the ones your community plays
          </p>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-2">
            {GAMES.map(g => {
              const active = games.includes(g.key);
              return (
                <button
                  key={g.key}
                  onClick={() => toggleGame(g.key)}
                  className={`flex items-center gap-2 px-3 py-2 rounded-lg border text-xs font-medium font-[Manrope] transition-all text-left ${
                    active
                      ? 'border-white/20 bg-white/8 text-white'
                      : 'border-white/6 bg-[var(--bg-hover)] text-zinc-500 hover:border-white/12 hover:text-zinc-300'
                  }`}
                >
                  <span
                    className="w-2.5 h-2.5 rounded-full shrink-0"
                    style={{ background: active ? g.color : '#3f3f46' }}
                  />
                  {g.label}
                  {active && <Check size={11} className="ml-auto shrink-0 text-white/60" />}
                </button>
              );
            })}
          </div>
          {games.length > 0 && (
            <p className="text-xs text-zinc-600 mt-2 font-[Manrope]">{games.length} game{games.length !== 1 ? 's' : ''} selected</p>
          )}
        </div>

        {/* Channel */}
        <div>
          <p className="text-xs font-semibold text-zinc-500 uppercase tracking-widest mb-2 font-[Manrope]">Post to channel</p>
          <ChannelSelect channels={channels} value={channelId} onChange={setChannelId} />
        </div>

        {/* Enable toggle */}
        <div className="flex items-center justify-between">
          <span className="text-sm text-zinc-400 font-[Manrope]">Alerts enabled</span>
          <button
            onClick={() => setEnabled(e => !e)}
            className={`relative w-11 h-6 rounded-full transition-colors ${enabled ? 'bg-[#5865F2]' : 'bg-white/10'}`}
          >
            <span className={`absolute top-1 w-4 h-4 bg-white rounded-full shadow transition-all ${enabled ? 'left-6' : 'left-1'}`} />
          </button>
        </div>

        <div className="flex gap-2">
          <button
            onClick={handleSave}
            disabled={saving || !channelId || games.length === 0}
            className="flex-1 flex items-center justify-center gap-2 py-2.5 rounded-lg bg-[#5865F2] hover:bg-[#4752C4] disabled:opacity-40 disabled:cursor-not-allowed text-sm font-semibold text-white font-[Outfit] transition-colors"
          >
            {saved ? <><Check size={16} /> Saved!</> : saving ? 'Saving…' : <><Save size={16} /> Save</>}
          </button>
          <button
            onClick={handleTest}
            disabled={testing || !channelId}
            title="Send a sample alert to the configured channel"
            className="flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg border border-[var(--border-md)] hover:bg-[var(--bg-hover)] disabled:opacity-40 disabled:cursor-not-allowed text-sm font-semibold text-zinc-300 font-[Outfit] transition-colors"
          >
            <Send size={15} />
            {testing ? 'Sending…' : 'Test'}
          </button>
          {hasConfig && games.includes('lol') && (
            <button
              onClick={handleCheckLolNow}
              disabled={checkingLol || !channelId}
              title="Fetch the latest LoL patch notes right now and post them"
              className="flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg border border-[var(--border-md)] hover:bg-[var(--bg-hover)] disabled:opacity-40 disabled:cursor-not-allowed text-sm font-semibold text-zinc-300 font-[Outfit] transition-colors"
            >
              <RefreshCw size={15} />
              {checkingLol ? 'Checking…' : 'Get Latest LoL Patch'}
            </button>
          )}
        </div>
      </div>
    </motion.div>
  );
}

export default function GameAlerts() {
  const { selectedGuildId } = useGuild();

  return (
    <div className="min-h-screen bg-[var(--bg-base)]">
      <Sidebar />
      <main className="lg:ml-60 pt-16 lg:pt-0 p-6" data-testid="game-alerts-main">
        <div className="mb-8">
          <h1 className="text-3xl font-bold font-[Outfit] text-white">Game Alerts</h1>
          <p className="text-zinc-500 text-sm mt-1 font-[Manrope]">
            Automatic free game giveaways and patch note announcements
          </p>
        </div>

        {!selectedGuildId ? (
          <div className="flex items-center justify-center min-h-[40vh]">
            <p className="text-zinc-500 font-[Manrope]">Select a server to configure game alerts.</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
            <FreeGameAlerts guildId={selectedGuildId} />
            <PatchNoteAlerts guildId={selectedGuildId} />
          </div>
        )}
      </main>
    </div>
  );
}
